import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';

import { addCartItemOptions, cartKey } from '@lib/api/options/cart';
import { MutationCallbackOptions } from '@lib/api/options/types';
import AppError from '@lib/app-error';
import { notifyCartItemsAdded } from '@lib/events/cart';
import { CART_ERROR_CODE, CartItemInput } from '@lib/types';
import { getErrorMessage } from '@lib/utils/error-handler';

import { refreshCartOnUnavailableOffer } from './refresh-cart-on-unavailable-offer';

export const useAddCartItem = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { mutateAsync: execute, isPending } = useMutation(addCartItemOptions());

  const onAddCartItem = (
    input: CartItemInput,
    options?: MutationCallbackOptions<Awaited<ReturnType<typeof execute>>>,
  ) =>
    execute(input, {
      onSuccess: async (result) => {
        queryClient
          .invalidateQueries({ queryKey: cartKey })
          .catch(() => undefined);
        notifyCartItemsAdded();
        options?.onCompleted?.(result);
      },
      onError: async (error) => {
        if (await refreshCartOnUnavailableOffer(queryClient, error)) {
          message.warning(
            'A oferta não está mais disponível e o carrinho foi atualizado.',
          );

          options?.onError?.(error);
          return;
        }

        if (
          error instanceof AppError &&
          (error.code === CART_ERROR_CODE.OFFER_LIMIT_REACHED ||
            error.code === CART_ERROR_CODE.STORE_PURCHASE_LIMIT_REACHED)
        ) {
          message.warning(
            getErrorMessage(
              error,
              'A quantidade solicitada excede o limite permitido.',
            ),
          );

          options?.onError?.(error);
          return;
        }

        message.error(
          getErrorMessage(
            error,
            'Não foi possível adicionar a oferta ao carrinho.',
          ),
        );
        options?.onError?.(error);
      },
    });

  return { addCartItem: onAddCartItem, addingCartItem: isPending };
};
